import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  PlusIcon,
  EyeIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  CalendarDaysIcon,
  BuildingOfficeIcon,
  UsersIcon,
  BanknotesIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  CheckBadgeIcon,
  TrashIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';
import {
  CheckCircleIcon as CheckCircleSolidIcon,
  XCircleIcon as XCircleSolidIcon,
  ClockIcon as ClockSolidIcon
} from '@heroicons/react/24/solid';
import { Helmet } from 'react-helmet-async';
import { useGetPayrollsQuery, useGeneratePayrollMutation, useApprovePayrollMutation, useMarkAsPaidPayrollMutation, useDeletePayrollMutation } from '../../services/payrollApi';
import { useGetMyCompanyQuery } from '../../services/companyApi';
import { updateFilters, setCurrentPayroll } from '../../store/slices/payrollSlice';
import PayrollDetailDialog from './PayrollDetailDialog';
import SalarySlipDialog from './SalarySlipDialog';

const PayrollManagement = () => {
  const { t } = useTranslation(['common', 'payroll']);
  const dispatch = useDispatch();
  const { filters, currentPayroll } = useSelector((state) => state.payroll);
  const { user } = useSelector((state) => state.auth);

  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [salarySlipDialogOpen, setSalarySlipDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [paidDialogOpen, setPaidDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPayrollId, setSelectedPayrollId] = useState(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [selectedPayrollForAction, setSelectedPayrollForAction] = useState(null);

  // Filter out null values for API call and add companyId
  const apiFilters = Object.fromEntries(
    Object.entries({
      ...filters,
      companyId: user?.companyId || filters.companyId
    }).filter(([key, value]) => value !== null && value !== '')
  );

  const { data: payrollsData, isLoading, refetch } = useGetPayrollsQuery(apiFilters);
  const [generatePayroll, { isLoading: generating }] = useGeneratePayrollMutation();
  const [approvePayroll, { isLoading: approving }] = useApprovePayrollMutation();
  const [markAsPaidPayroll, { isLoading: markingPaid }] = useMarkAsPaidPayrollMutation();
  const [deletePayroll, { isLoading: deleting }] = useDeletePayrollMutation();
  
  // Company query hook
  const companyQuery = useGetMyCompanyQuery();
  const { data: companyData, isLoading: companyLoading, error: companyError, refetch: refetchCompany } = companyQuery;
  const getCompanyDisplayValue = () => {
    if (companyLoading) return 'Loading...';
    if (companyError) return 'Error loading company';
    if (!companyData) return 'Company not found';
    return companyData.name;
  };

  const getCompanyHelperText = () => {
    if (companyError) return 'Please try refreshing or contact administrator';
    if (!companyData && !companyLoading) return 'No company assigned to your account';
    return 'Payroll will be generated for your current company';
  };

  const isCompanyValid = () => {
    return !companyLoading && !companyError && companyData && user?.companyId;
  };

  // Generate year options starting from current year (only current and future)
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = 0; i < 8; i++) {
    years.push(currentYear + i);
  }

  const [generateForm, setGenerateForm] = useState({
    month: new Date().getMonth() + 1,
    year: currentYear,
  });

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  const handleGeneratePayroll = async () => {
    try {
      const payrollData = {
        ...generateForm,
        companyId: user?.companyId,
      };
      await generatePayroll(payrollData).unwrap();
      setGenerateDialogOpen(false);
      refetch();
      setGenerateForm({
        month: new Date().getMonth() + 1,
        year: currentYear,
      });
    } catch (error) {
      console.error('Failed to generate payroll:', error);
    }
  };

  const handleApprovePayroll = async () => {
    if (!selectedPayrollForAction) return;
    
    try {
      await approvePayroll({ id: selectedPayrollForAction._id }).unwrap();
      setApproveDialogOpen(false);
      setSelectedPayrollForAction(null);
      refetch();
    } catch (error) {
      console.error('Failed to approve payroll:', error);
    }
  };

  const handleMarkAsPaidPayroll = async () => {
    if (!selectedPayrollForAction) return;
    
    try {
      await markAsPaidPayroll({ id: selectedPayrollForAction._id }).unwrap();
      setPaidDialogOpen(false);
      setSelectedPayrollForAction(null);
      refetch();
    } catch (error) {
      console.error('Failed to mark payroll as paid:', error);
    }
  };

  const handleDeletePayroll = async () => {
    if (!selectedPayrollForAction) return;
    
    try {
      await deletePayroll(selectedPayrollForAction._id).unwrap();
      setDeleteDialogOpen(false);
      setSelectedPayrollForAction(null);
      refetch();
    } catch (error) {
      console.error('Failed to delete payroll:', error);
    }
  };

  const openApproveDialog = (payroll) => {
    setSelectedPayrollForAction(payroll);
    setApproveDialogOpen(true);
  };

  const openPaidDialog = (payroll) => {
    setSelectedPayrollForAction(payroll);
    setPaidDialogOpen(true);
  };

  const openDeleteDialog = (payroll) => {
    setSelectedPayrollForAction(payroll);
    setDeleteDialogOpen(true);
  };

  const handleViewPayroll = (payroll) => {
    dispatch(setCurrentPayroll(payroll));
    setDetailDialogOpen(true);
  };

  const handleViewSalarySlip = (payrollId, employeeId) => {
    setSelectedPayrollId(payrollId);
    setSelectedEmployeeId(employeeId);
    setSalarySlipDialogOpen(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'calculated':
        return 'bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 border-blue-200';
      case 'approved':
        return 'bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 border-green-200';
      case 'paid':
        return 'bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 border-purple-200';
      default:
        return 'bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 border-blue-200'; // Default to calculated
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount || 0);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
      <Helmet>
        <title>{t('payroll:payrollManagement', 'Payroll Management')} - {t('common:appName', 'IAR HRMS')}</title>
      </Helmet>

      {/* Company Error Alert */}
      {companyError && (
        <div className="mb-6 p-4 bg-gradient-to-r from-red-50 to-rose-50 border border-red-200 rounded-2xl shadow-sm">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-red-100 rounded-xl flex items-center justify-center">
              <ExclamationTriangleIcon className="h-4 w-4 text-red-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-red-800">Company Information Error</h3>
              <p className="text-sm text-red-700 mt-1">Unable to load company information. Please try refreshing the page or contact your administrator.</p>
            </div>
          </div>
        </div>
      )}

      {!companyData && !companyLoading && !companyError && (
        <div className="mb-6 p-4 bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200 rounded-2xl shadow-sm">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-yellow-100 rounded-xl flex items-center justify-center">
              <ExclamationTriangleIcon className="h-4 w-4 text-yellow-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-yellow-800">Company Assignment Required</h3>
              <p className="text-sm text-yellow-700 mt-1">No company is assigned to your account. Please contact your administrator to assign a company.</p>
            </div>
          </div>
        </div>
      )}

      {/* Header Section */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Payroll Management</h1>
            <p className="text-gray-600">Manage and process payroll for your organization</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={refetch}
              disabled={isLoading}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md"
            >
              <ArrowPathIcon className="h-4 w-4 mr-2" />
              Refresh
            </button>
            <button
              onClick={() => setGenerateDialogOpen(true)}
              className="inline-flex items-center px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Generate Payroll
            </button>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-8 hover:shadow-xl transition-shadow duration-300">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl flex items-center justify-center">
            <FunnelIcon className="h-5 w-5 text-blue-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900">Filters</h2>
          <button
            onClick={() => dispatch(updateFilters({ month: null, year: null, status: null }))}
            className="ml-auto px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            Clear Filters
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Month</label>
            <select
              value={filters.month || ''}
              onChange={(e) => dispatch(updateFilters({ month: e.target.value === '' ? null : e.target.value }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
            >
              <option value="">All Months</option>
              {months.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Year</label>
            <select
              value={filters.year || ''}
              onChange={(e) => dispatch(updateFilters({ year: e.target.value === '' ? null : e.target.value }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
            >
              <option value="">All Years</option>
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
            <select
              value={filters.status || ''}
              onChange={(e) => dispatch(updateFilters({ status: e.target.value === '' ? null : e.target.value }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
            >
              <option value="">All Status</option>
              <option value="calculated">Calculated</option>
              <option value="approved">Approved</option>
              <option value="paid">Paid</option>
            </select>
          </div>
        </div>
      </div>

      {/* Payroll Records Section */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-shadow duration-300">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl flex items-center justify-center">
              <BanknotesIcon className="h-5 w-5 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Payroll Records</h2>
          </div>
        </div>

        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent"></div>
              <span className="ml-3 text-gray-600">Loading payroll records...</span>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gradient-to-r from-gray-50 to-slate-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Payroll ID</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Period</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Company</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Employees</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Gross Pay</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Net Pay</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {payrollsData?.data?.map((payroll) => (
                  <tr key={payroll._id} className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-colors duration-200">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-lg flex items-center justify-center mr-3">
                          <CalendarDaysIcon className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <div className="text-sm font-bold text-gray-900">{payroll.payrollId}</div>
                          <div className="text-xs text-gray-500">ID: {payroll._id.slice(-8)}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{payroll.formattedPeriod}</div>
                      <div className="text-xs text-gray-500">Monthly Payroll</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-6 h-6 bg-gradient-to-br from-green-100 to-emerald-100 rounded-lg flex items-center justify-center mr-2">
                          <BuildingOfficeIcon className="h-3 w-3 text-green-600" />
                        </div>
                        <span className="text-sm font-medium text-gray-900">{payroll.companyId?.name || 'N/A'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-6 h-6 bg-gradient-to-br from-purple-100 to-pink-100 rounded-lg flex items-center justify-center mr-2">
                          <UsersIcon className="h-3 w-3 text-purple-600" />
                        </div>
                        <span className="text-sm font-bold text-gray-900">{payroll.totalEmployees}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold text-green-700">{formatCurrency(payroll.totalGrossPay)}</div>
                      <div className="text-xs text-gray-500">Total Earnings</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold text-blue-700">{formatCurrency(payroll.totalNetPay)}</div>
                      <div className="text-xs text-gray-500">After Deductions</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`inline-flex px-3 py-1 rounded-full text-xs font-bold shadow-sm border ${getStatusColor(payroll.status)}`}>
                        <div className="flex items-center space-x-1">
                          {payroll.status === 'approved' && <CheckCircleIcon className="h-3 w-3" />}
                          {payroll.status === 'calculated' && <ClockIcon className="h-3 w-3" />}
                          {payroll.status === 'paid' && <CurrencyDollarIcon className="h-3 w-3" />}
                          <span className="capitalize">{payroll.status}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleViewPayroll(payroll)}
                          className="inline-flex items-center px-3 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-medium rounded-lg shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200"
                          title="View Details"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        {payroll.status === 'calculated' && (
                          <button
                            onClick={() => openApproveDialog(payroll)}
                            className="inline-flex items-center px-3 py-2 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-medium rounded-lg shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200"
                            title="Approve Payroll"
                          >
                            <CheckCircleIcon className="h-4 w-4" />
                          </button>
                        )}
                        {payroll.status === 'approved' && (
                          <button
                            onClick={() => openPaidDialog(payroll)}
                            className="inline-flex items-center px-3 py-2 bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white font-medium rounded-lg shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200"
                            title="Mark as Paid"
                          >
                            <CurrencyDollarIcon className="h-4 w-4" />
                          </button>
                        )}
                        {payroll.status !== 'approved' && payroll.status !== 'paid' && (
                          <button
                            onClick={() => openDeleteDialog(payroll)}
                            className="inline-flex items-center px-3 py-2 bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white font-medium rounded-lg shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200"
                            title="Delete Payroll"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {(!payrollsData?.data || payrollsData.data.length === 0) && (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <BanknotesIcon className="h-8 w-8 text-gray-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">No Payroll Records Found</h3>
                      <p className="text-gray-600">Generate your first payroll to get started.</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Generate Payroll Modal */}
      {generateDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl flex items-center justify-center">
                  <PlusIcon className="h-5 w-5 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Generate Payroll</h3>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Month</label>
                  <select
                    value={generateForm.month}
                    onChange={(e) => setGenerateForm({ ...generateForm, month: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm"
                  >
                    {months.map((month) => (
                      <option key={month.value} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Year</label>
                  <select
                    value={generateForm.year}
                    onChange={(e) => setGenerateForm({ ...generateForm, year: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm"
                  >
                    {years.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                <div className="flex items-center space-x-2 mb-2">
                  <BuildingOfficeIcon className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-semibold text-blue-700">Company</span>
                </div>
                <p className="text-sm text-blue-900">{getCompanyDisplayValue()}</p>
                <p className="text-xs text-blue-600 mt-1">{getCompanyHelperText()}</p>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setGenerateDialogOpen(false)}
                className="px-6 py-2 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleGeneratePayroll}
                disabled={generating || !isCompanyValid()}
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {generating ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Generating...
                  </div>
                ) : (
                  'Generate'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payroll Detail Dialog */}
      <PayrollDetailDialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        payroll={currentPayroll}
        onViewSalarySlip={handleViewSalarySlip}
      />

      {/* Salary Slip Dialog */}
      <SalarySlipDialog
        open={salarySlipDialogOpen}
        onClose={() => setSalarySlipDialogOpen(false)}
        payrollId={selectedPayrollId}
        employeeId={selectedEmployeeId}
      />

      {/* Approve Confirmation Modal */}
      {approveDialogOpen && selectedPayrollForAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl flex items-center justify-center">
                  <CheckCircleIcon className="h-5 w-5 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Approve Payroll</h3>
              </div>
            </div>

            <div className="p-6">
              <div className="flex items-start space-x-3 mb-4">
                <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <ExclamationTriangleIcon className="h-4 w-4 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-700 mb-2">
                    Are you sure you want to approve this payroll? This action cannot be undone.
                  </p>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-sm text-gray-600">
                      <strong>Payroll ID:</strong> {selectedPayrollForAction.payrollId}
                    </div>
                    <div className="text-sm text-gray-600">
                      <strong>Period:</strong> {selectedPayrollForAction.formattedPeriod}
                    </div>
                    <div className="text-sm text-gray-600">
                      <strong>Employees:</strong> {selectedPayrollForAction.totalEmployees}
                    </div>
                    <div className="text-sm text-gray-600">
                      <strong>Total Amount:</strong> {formatCurrency(selectedPayrollForAction.totalNetPay)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setApproveDialogOpen(false);
                  setSelectedPayrollForAction(null);
                }}
                className="px-6 py-2 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleApprovePayroll}
                disabled={approving}
                className="px-6 py-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {approving ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Approving...
                  </div>
                ) : (
                  'Approve Payroll'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mark as Paid Confirmation Modal */}
      {paidDialogOpen && selectedPayrollForAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl flex items-center justify-center">
                  <CurrencyDollarIcon className="h-5 w-5 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Mark as Paid</h3>
              </div>
            </div>

            <div className="p-6">
              <div className="flex items-start space-x-3 mb-4">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <ExclamationTriangleIcon className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-700 mb-2">
                    Are you sure you want to mark this payroll as paid? This will update the payment status for all employees in this payroll.
                  </p>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-sm text-gray-600">
                      <strong>Payroll ID:</strong> {selectedPayrollForAction.payrollId}
                    </div>
                    <div className="text-sm text-gray-600">
                      <strong>Period:</strong> {selectedPayrollForAction.formattedPeriod}
                    </div>
                    <div className="text-sm text-gray-600">
                      <strong>Employees:</strong> {selectedPayrollForAction.totalEmployees}
                    </div>
                    <div className="text-sm text-gray-600">
                      <strong>Total Amount:</strong> {formatCurrency(selectedPayrollForAction.totalNetPay)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setPaidDialogOpen(false);
                  setSelectedPayrollForAction(null);
                }}
                className="px-6 py-2 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleMarkAsPaidPayroll}
                disabled={markingPaid}
                className="px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {markingPaid ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Marking as Paid...
                  </div>
                ) : (
                  'Mark as Paid'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteDialogOpen && selectedPayrollForAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-red-100 to-rose-100 rounded-xl flex items-center justify-center">
                  <TrashIcon className="h-5 w-5 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Delete Payroll</h3>
              </div>
            </div>

            <div className="p-6">
              <div className="flex items-start space-x-3 mb-4">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <ExclamationTriangleIcon className="h-4 w-4 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-700 mb-2">
                    Are you sure you want to delete this payroll? This action cannot be undone and all associated data will be permanently removed.
                  </p>
                  <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                    <div className="text-sm text-red-800">
                      <strong>Payroll ID:</strong> {selectedPayrollForAction.payrollId}
                    </div>
                    <div className="text-sm text-red-800">
                      <strong>Period:</strong> {selectedPayrollForAction.formattedPeriod}
                    </div>
                    <div className="text-sm text-red-800">
                      <strong>Status:</strong> <span className="capitalize">{selectedPayrollForAction.status}</span>
                    </div>
                    <div className="text-sm text-red-800">
                      <strong>Employees:</strong> {selectedPayrollForAction.totalEmployees}
                    </div>
                  </div>
                  <p className="text-xs text-red-600 mt-2">
                    ⚠️ This will permanently delete all payroll calculations and cannot be recovered.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setDeleteDialogOpen(false);
                  setSelectedPayrollForAction(null);
                }}
                className="px-6 py-2 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleDeletePayroll}
                disabled={deleting}
                className="px-6 py-2 bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleting ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Deleting...
                  </div>
                ) : (
                  'Delete Payroll'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollManagement;
